import React from 'react';
import { titleize } from 'inflection';
import moment from 'moment';

import Button, { BUTTON_SIZES } from 'buyside/components/presentation/Button/button';
import CardFeedHighlight from 'buyside/components/presentation/CardFeed/CardFeedHighlight/cardFeedHighlight';
import EntityShowJoyride from 'buyside/components/presentation/Joyride/EntityShowJoyride/entityShowJoyride';
import FollowButton, {
  FollowableType
} from 'buyside/components/presentation/FollowButton/followButton';
import LoadingSpinner, {
  SIZES
} from 'buyside/components/presentation/LoadingSpinner/loadingSpinner';
import QuickPosition from 'buyside/components/presentation/QuickPosition/quickPosition';
import { EntityShowJoyrideTarget } from 'buyside/components/presentation/Joyride/EntityShowJoyride/entityShowJoyrideHelper';

import {
  useEntityOverviewQuery,
  useEntityOverviewCurrentUserQuery
} from 'interfaces/graphql-typings';

import { adminEntityEdit } from 'buyside/util/urlHelper';

import styles from 'buyside/components/presentation/CompanyShowPage/companyShowPageStyles.scss';

const EntityShowOverview: React.FC<{ id: string; isPublic: boolean }> = ({
  id,
  isPublic
}): React.ReactElement => {
  const { data, loading, error } = useEntityOverviewQuery({ variables: { id } });

  const {
    data: userData,
    loading: userLoading,
    error: userError
  } = useEntityOverviewCurrentUserQuery({
    skip: isPublic
  });

  if (loading || userLoading) return <LoadingSpinner size={SIZES.MEDIUM} />;

  if (error || userError || !data?.entity) throw error;

  const { name, primarySymbol, primaryAsset } = data.entity;

  return (
    <div className={styles.overviewContainer}>
      <div className={styles.overviewUpper}>
        <div className={styles.nameSymbol}>
          <h3>{primarySymbol}</h3>
          {name && <div className={styles.name}>{titleize(name)}</div>}
          {!isPublic && (
            <FollowButton
              type={FollowableType.ENTITY}
              buttonDisplay="Company"
              id={id}
              size={BUTTON_SIZES.TINIEST}
            />
          )}
        </div>
        <div className={styles.priceContainer}>
          <div className={styles.priceLabel}>Last Closing Price</div>
          {primaryAsset?.latestPrice ? (
            <div className={styles.priceVal}>{primaryAsset.latestPrice}</div>
          ) : (
            <div className={styles.pending}>Pending...</div>
          )}
          {primaryAsset?.latestQuoteDate && (
            <div className={styles.priceDate}>
              {moment(new Date(`${primaryAsset.latestQuoteDate}T00:00:00.000-05:00`)).format(
                'MM/DD/YYYY'
              )}
            </div>
          )}
        </div>
      </div>
      <CardFeedHighlight>
        <span>
          Sellside Consensus:{' '}
          <strong>{primaryAsset?.fundamental?.sellsideConsensusText || 'N/A'}</strong>
        </span>
      </CardFeedHighlight>
      {!isPublic && userData?.currentUser?.isAdmin && !primaryAsset && (
        <div className={styles.adminMsg}>
          <strong>Admin Only</strong>
          <div>This entity doesn't have any assets.</div>
          <Button text="Create One Now" url={adminEntityEdit(id)} />
        </div>
      )}
      {!isPublic && primaryAsset?.id ? (
        <div
          id="quick-positions"
          data-joyride-target={EntityShowJoyrideTarget.CREATE_QUICK_POSITION}
        >
          <QuickPosition assetId={primaryAsset.id} />
        </div>
      ) : null}
      <EntityShowJoyride />
    </div>
  );
};

export default EntityShowOverview;
